/**
 * Custom request pre-confirmation checkout page.
 * Shows preview pricing, terms, cancellation policy, and submit button.
 * @module Widget/CustomRequest/CustomRequestCheckoutPage
 */

import React, { Suspense, useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAtomValue } from 'jotai';
import { loadStripe } from '@stripe/stripe-js';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezone);
import {
  useWidgetState,
  useRestaurantId,
  AuthAtom,
  RestaurantAtom,
  PaymentMethodsByIdAtom,
  usePaymentMethods,
} from '../lib/state.ts';
import { previewCustomRequest, submitCustomRequest, getRestaurant, getCustomRequestSettings } from '@/lib/api.ts';
import { track } from '@/lib/analytics/index.ts';
import { IS_EMBED } from '@/lib/isEmbed.ts';
import type { ConfirmationResponseReservation, RestaurantResponseData } from '@/lib/types.ts';
import NeedToKnows from '@/components/NeedToKnows/NeedToKnows.tsx';
import Card, { type CardNames } from '@/components/Card/Card.tsx';
import Config from '@/lib/Config.ts';
import { formatTimeRange, saveRequestSnapshot } from './utils.ts';
import { TERMS_OF_USE, PRIVACY_POLICY } from '../Checkout/legalContent.ts';
import styles from './CustomRequest.module.css';
import checkoutStyles from '../Checkout/CheckoutPage.module.css';

/**
 * Renders markdown-style bold text (**text**) as <strong> elements.
 */
function renderBoldText(text: string): React.ReactNode[] {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return <strong key={i} className="font-semibold">{part.slice(2, -2)}</strong>;
    }
    return <span key={i}>{part}</span>;
  });
}

/**
 * Pre-confirmation checkout page for custom requests.
 * Fetches preview, displays summary, handles submission with 3DS support.
 *
 * @returns Rendered checkout page
 */
/**
 * Reusable modal for displaying Terms of Use / Privacy Policy.
 */
function ContentModal({
  isOpen,
  onClose,
  title,
  content,
}: {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  content?: string;
}) {
  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    // In embed mode the overlay owns scroll-lock — don't mutate the host page body.
    if (!IS_EMBED) document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      if (!IS_EMBED) document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div className={checkoutStyles.modalOverlay} role="dialog" aria-modal="true" aria-label={title}>
      <div className={checkoutStyles.modalBackdrop} onClick={onClose} aria-hidden="true" />
      <div className={checkoutStyles.modalPanel}>
        <div className={checkoutStyles.modalHeader}>
          <h2 className={checkoutStyles.modalTitle}>{title}</h2>
          <button type="button" onClick={onClose} className={checkoutStyles.modalClose} aria-label="Close">
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
              <path d="M1 1L13 13M13 1L1 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
          </button>
        </div>
        <div className={checkoutStyles.modalContent}>
          {content
            ? content.split('\n').filter(Boolean).map((paragraph, i) => <p key={i}>{paragraph}</p>)
            : null}
        </div>
      </div>
    </div>
  );
}

const CustomRequestCheckoutPage: React.FC = () => {
  usePaymentMethods();
  const navigate = useNavigate();
  const restaurantId = useRestaurantId();
  const storedRestaurant = useAtomValue(RestaurantAtom);
  const auth = useAtomValue(AuthAtom);
  const [widget] = useWidgetState();
  const methodsById = useAtomValue(PaymentMethodsByIdAtom);

  const [restaurant, setLocalRestaurant] = useState<RestaurantResponseData | null>(storedRestaurant);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [dorsiaTermsOpen, setDorsiaTermsOpen] = useState(false);
  const [privacyPolicyOpen, setPrivacyPolicyOpen] = useState(false);

  useEffect(() => {
    if (storedRestaurant) {
      setLocalRestaurant(storedRestaurant);
      return;
    }
    if (!restaurantId) return;
    const date = widget.custom_request?.date ?? new Date().toISOString().slice(0, 10);
    void getRestaurant(restaurantId, date, '0:00', 'checkout', auth?.token ?? null).then((res) => {
      if (res?.data) setLocalRestaurant(res.data);
    });
  }, [restaurantId, storedRestaurant, auth?.token]);

  useEffect(() => {
    if (!auth?.token) {
      navigate('../request', { replace: true });
    }
  }, [auth?.token, navigate]);

  const cr = widget.custom_request;
  const [preview, setPreview] = useState<ConfirmationResponseReservation | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resolvedTableTypeNames, setResolvedTableTypeNames] = useState<string[]>(
    cr?.selected_table_type_names ?? []
  );

  useEffect(() => {
    if (resolvedTableTypeNames.length > 0 || !cr?.selected_table_types?.length || !restaurantId) return;
    void getCustomRequestSettings(restaurantId, auth?.token ?? null).then((res) => {
      const opts = res?.data?.customRequestSettings?.table_options ?? [];
      if (opts.length > 0 && cr.selected_table_types.length > 0) {
        const names = cr.selected_table_types.map((id) => {
          const match = opts.find((o) => Number(o.id) === Number(id));
          return match?.name ?? `Type ${id}`;
        });
        setResolvedTableTypeNames(names);
      }
    });
  }, [restaurantId, auth?.token, cr?.selected_table_types, resolvedTableTypeNames.length]);

  useEffect(() => {
    if (!restaurantId || !auth?.token || !cr) return;

    const loadPreview = async () => {
      try {
        setLoading(true);
        const response = await previewCustomRequest(auth.token, restaurantId, {
          party: cr.party_size,
          date: cr.date,
          times: cr.selected_times,
          spend: cr.per_person_spend,
          table_types: cr.selected_table_types.length > 0 ? cr.selected_table_types : undefined,
          expiration_hours: cr.expiration_hours,
        });
        setPreview(response.reservation);
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : 'Failed to load preview';
        setError(message);
      } finally {
        setLoading(false);
      }
    };

    void loadPreview();
  }, [restaurantId, auth?.token, cr?.date, cr?.party_size, cr?.per_person_spend]);

  const cancellationPolicies = (preview?.cancellation_policies ?? [])
    .filter((p) => !p.includes('Dorsia app'));

  /** Builds and saves a localStorage snapshot for the submitted page. */
  const snapshotAndNavigate = (uuid: string) => {
    const pm = widget.payment_method ? methodsById[widget.payment_method] : null;
    saveRequestSnapshot(uuid, {
      date: cr!.date,
      party_size: cr!.party_size,
      selected_times: cr!.selected_times,
      selected_table_types: cr!.selected_table_types,
      selected_table_type_names: cr!.selected_table_type_names,
      per_person_spend: cr!.per_person_spend,
      minimum_spend: cr!.per_person_spend * cr!.party_size,
      expiration_hours: cr!.expiration_hours,
      expiration_description: cr!.expiration_description,
      comments: cr!.comments,
      payment_brand: pm?.brand,
      payment_last4: pm?.number,
      user_first_name: widget.first_name,
      user_last_name: widget.last_name,
      user_email: widget.email,
      user_phone: widget.phone_number,
    });
    if (restaurantId != null) {
      track('Widget Book Succeeded', {
        restaurant_id: restaurantId,
        booking_type: 'custom_request',
        reservation_uuid: uuid,
      });
    }
    navigate(`../request/submitted/${uuid}`);
  };

  const handleSubmit = useCallback(async () => {
    if (!restaurantId || !auth?.token || !cr || !widget.payment_method) return;

    setSubmitting(true);
    setError(null);

    track('Widget Book Pressed', {
      restaurant_id: restaurantId,
      booking_type: 'custom_request',
    });

    // Custom requests submit via submitCustomRequest (not useSubmitBooking), so
    // the book-outcome events are fired here, once per failed/successful attempt.
    const trackFailed = (errorCode: string) =>
      track('Widget Book Failed', {
        restaurant_id: restaurantId,
        booking_type: 'custom_request',
        error_code: errorCode,
      });

    try {
      const response = await submitCustomRequest(auth.token, restaurantId, {
        date: cr.date,
        times: cr.selected_times,
        party: cr.party_size,
        minimum_spend: cr.per_person_spend * cr.party_size,
        per_person_spend: cr.per_person_spend,
        payment_method_id: widget.payment_method,
        table_types: cr.selected_table_types.length > 0 ? cr.selected_table_types : undefined,
        expiration_hours: cr.expiration_hours,
        comments: cr.comments,
        ...(widget.referral_code && { referral_code: widget.referral_code }),
      });

      const errorResponse = response as unknown as { message?: string };
      if (!response?.reservation?.uuid) {
        trackFailed('no_reservation_in_response');
        setError(errorResponse?.message ?? 'Something went wrong. Please try again.');
        return;
      }

      if (response.status !== 'OK') {
        trackFailed('status_not_ok');
        setError(errorResponse?.message ?? 'The request could not be completed. Please try again.');
        return;
      }

      const uuid = response.reservation.uuid.trim();
      if (!uuid || uuid.length < 10) {
        trackFailed('invalid_reservation_uuid');
        setError('Invalid reservation reference. Please contact support.');
        return;
      }

      snapshotAndNavigate(uuid);
    } catch (err: unknown) {
      if (err instanceof Error && 'response' in err) {
        const apiErr = err as {
          message?: string;
          response: {
            payment_intent_client_secret?: string;
            stripe_public_key?: string;
            message?: string;
            error?: string;
            type?: string;
          };
        };
        const resp = apiErr.response;

        if (resp.payment_intent_client_secret) {
          try {
            const stripeKey = resp.stripe_public_key || Config.data?.stripe?.key;
            if (!stripeKey) throw new Error('Stripe not configured');
            const stripe = await loadStripe(stripeKey);
            if (!stripe) throw new Error('Failed to load Stripe');

            const result = await stripe.confirmCardPayment(resp.payment_intent_client_secret);
            if (result.error) {
              trackFailed(result.error.code ?? 'card_authentication_failed');
              setError(result.error.message ?? 'Payment authentication failed.');
            } else {
              const retryResponse = await submitCustomRequest(auth.token, restaurantId, {
                date: cr.date,
                times: cr.selected_times,
                party: cr.party_size,
                minimum_spend: cr.per_person_spend * cr.party_size,
                per_person_spend: cr.per_person_spend,
                payment_method_id: widget.payment_method,
                table_types: cr.selected_table_types.length > 0 ? cr.selected_table_types : undefined,
                expiration_hours: cr.expiration_hours,
                comments: cr.comments,
                ...(widget.referral_code && { referral_code: widget.referral_code }),
              });
              if (!retryResponse?.reservation?.uuid || retryResponse.status !== 'OK') {
                const retryErrMsg = (retryResponse as unknown as { message?: string })?.message;
                trackFailed('retry_after_3ds_failed');
                setError(retryErrMsg ?? 'Something went wrong after payment verification. Please try again.');
                return;
              }
              const uuid = retryResponse.reservation.uuid.trim();
              if (!uuid || uuid.length < 10) {
                trackFailed('invalid_reservation_uuid_after_3ds');
                setError('Invalid reservation reference after payment verification. Please contact support.');
                return;
              }

              snapshotAndNavigate(uuid);
            }
          } catch (stripeErr) {
            trackFailed('stripe_confirm_failed');
            setError(stripeErr instanceof Error ? stripeErr.message : 'Payment authentication failed.');
          }
          return;
        }

        const detail = resp.message || resp.error || apiErr.message;
        trackFailed(resp.type ?? 'submit_custom_request_failed');
        setError(detail ?? 'Failed to submit request. Please try again.');
        return;
      }

      trackFailed('unexpected_error');
      setError(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }, [restaurantId, auth?.token, cr, widget.payment_method, methodsById, navigate]);

  if (loading) {
    return (
      <div className={styles.page}>
        <div className={styles.card}>
          <div className="flex flex-col items-center gap-4 py-16">
            <div className={styles.spinner} />
            <p className={styles.sectionSubtitle}>Loading preview...</p>
          </div>
        </div>
      </div>
    );
  }

  const currencySymbol = restaurant?.currency_symbol ?? '$';

  return (
    <div className={styles.page}>
      <div className={styles.card}>
        {/* Back button */}
        <button className={styles.backButton} onClick={() => navigate('../payment')}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M19 12H5M12 19l-7-7 7-7" />
          </svg>
          Back
        </button>

        {/* Title */}
        <h2 className={styles.sectionTitle}>Finalize your request</h2>

        {/* Request summary */}
        <div className="flex flex-col gap-3">
          <div className={styles.summaryRow}>
            <span className={styles.summaryLabel}>Restaurant</span>
            <span className={styles.summaryValue}>{restaurant?.name}</span>
          </div>
          <div className={styles.summaryRow}>
            <span className={styles.summaryLabel}>Date</span>
            <span className={styles.summaryValue}>{preview?.date ?? (cr?.date ? dayjs(cr.date).format('dddd, MMMM D, YYYY') : '')}</span>
          </div>
          <div className={styles.summaryRow}>
            <span className={styles.summaryLabel}>Time Frame</span>
            <span className={styles.summaryValue}>{preview?.window ?? (cr ? formatTimeRange(cr.selected_times) : '')}</span>
          </div>
          {cr?.selected_table_types && cr.selected_table_types.length > 0 && (
            <div className={styles.summaryRow}>
              <span className={styles.summaryLabel}>Table Type</span>
              <span className={`${styles.summaryValue} max-w-[60%] truncate`} title={
                resolvedTableTypeNames.length > 0
                  ? resolvedTableTypeNames.join(', ')
                  : cr.selected_table_types.join(', ')
              }>
                {resolvedTableTypeNames.length > 0
                  ? resolvedTableTypeNames.join(', ')
                  : cr.selected_table_types.join(', ')}
              </span>
            </div>
          )}
          <div className={styles.summaryRow}>
            <span className={styles.summaryLabel}>Request Expires</span>
            <span className={styles.summaryValue}>
              {cr?.expiration_hours
                ? dayjs().add(cr.expiration_hours, 'hour')
                    .tz(restaurant?.timezone ?? 'America/New_York')
                    .format('MMM D, h:mm A z')
                : 'No limit'}
            </span>
          </div>

          <div className={styles.summaryRow}>
            <span className={styles.summaryLabel}>Party Size</span>
            <span className={styles.summaryValue}>{preview?.party ?? cr?.party_size}</span>
          </div>
          <div className={styles.summaryRow}>
            <span className={styles.summaryLabel}>Min. Spend / Person</span>
            <span className={styles.summaryValue}>{preview?.price_per_person__currency ?? `${currencySymbol}${cr?.per_person_spend}`}</span>
          </div>
          <div className={styles.summaryRow}>
            <span className={styles.summaryLabel}>Minimum Spend</span>
            <span className={styles.summaryValue}>
              {preview?.fee_restaurant__currency ?? `${currencySymbol}${(cr?.per_person_spend ?? 0) * (cr?.party_size ?? 0)}`}
            </span>
          </div>
          {preview?.points_used_currency_value__currency && (
            <div className={styles.summaryRow}>
              <span className={styles.summaryLabel}>Fun Coupons</span>
              <span className={styles.summaryValue}>-{preview.points_used_currency_value__currency}</span>
            </div>
          )}
          {preview && preview.credits_used > 0 && preview.credits_used__currency && (
            <div className={styles.summaryRow}>
              <span className={styles.summaryLabel}>Credits</span>
              <span className={styles.summaryValue}>-{preview.credits_used__currency}</span>
            </div>
          )}
          {preview?.tax_amount !== undefined && preview.tax_amount > 0 && (
            <div className={styles.summaryRow}>
              <span className={styles.summaryLabel}>Tax</span>
              <span className={styles.summaryValue}>{preview.tax_amount__currency}</span>
            </div>
          )}
          <div className={styles.summaryTotal}>
            <span className={styles.summaryTotalLabel}>Total</span>
            <span className={styles.summaryTotalValue}>
              {preview?.total__currency ?? `${currencySymbol}${(cr?.per_person_spend ?? 0) * (cr?.party_size ?? 0)}`}
            </span>
          </div>

          {/* Payment method */}
          {(() => {
            const pm = widget.payment_method ? methodsById[widget.payment_method] : null;
            return pm ? (
              <div className={styles.summaryRow}>
                <span className={styles.summaryLabel}>
                  <span className="inline-flex items-center gap-1.5">
                    <span className="w-7 inline-flex flex-shrink-0">
                      <Card name={(pm.brand?.toLowerCase() ?? 'visa') as CardNames} className="" />
                    </span>
                    ending in {pm.number}
                  </span>
                </span>
              </div>
            ) : null;
          })()}
        </div>
      </div>

      {/* Custom Request Terms */}
      <div className={styles.termsSection}>
        <h3 className={styles.termsTitle}>Custom Request Terms</h3>
        <div className={styles.termsBody}>
          <p>
            If you do not receive a response from the restaurant
            {cr?.expiration_description && cr.expiration_description !== 'No limit'
              ? ` within ${cr.expiration_description.toLowerCase()}`
              : ' in time'}
            , your request will be canceled and you will not be obligated to proceed with the reservation and associated table minimum spend.
          </p>
          <p>
            If your custom request is confirmed by the restaurant, your booking is confirmed and will be subject to the cancellation policy below.
            You are able to cancel this custom request anytime prior to the restaurant responding to your request.
          </p>
          <p>
            The restaurant may also propose a different reservation option, offering a new start time, minimum spend, or number of guests.
            If the restaurant proposes any of these options, you will have 30 minutes upon notification and text message to accept or reject their proposal.
          </p>
          <p>
            You will not be charged until the restaurant confirms your request. If it is not accepted, your spend commitment will be released.
          </p>
          <p className="font-medium">You will be charged upon booking confirmation.</p>
          <p className="opacity-60">Tax, tip and any overages are not included.</p>
        </div>
      </div>

      {/* Cancellation Policy */}
      {cancellationPolicies.length > 0 && (
        <div className={styles.termsSection}>
          <h3 className={styles.termsTitle}>Cancellation &amp; Modification Policy</h3>
          <ul className={styles.termsList}>
            {cancellationPolicies.map((policy, i) => (
              <li key={i}>{renderBoldText(policy)}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Need to know */}
      {restaurant?.need_to_knows && restaurant.need_to_knows.length > 0 && (
        <div className={styles.termsSection}>
          <h3 className={styles.termsTitle}>Need to know</h3>
          <Suspense fallback={<div>Loading...</div>}>
            <NeedToKnows need_to_knows={restaurant.need_to_knows} />
          </Suspense>
        </div>
      )}

      {/* Terms checkbox + Submit — sticky footer */}
      <div className={styles.stickyFooter}>
        {error && <div className={styles.errorBanner}>{error}</div>}

        <label className={styles.termsCheckboxRow}>
          <input
            type="checkbox"
            checked={termsAccepted}
            onChange={(e) => setTermsAccepted(e.target.checked)}
            className={styles.termsCheckboxHidden}
          />
          <span className={styles.termsCheckboxVisual}>
            <svg className={styles.termsCheckboxCheck} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <polyline points="20 6 9 17 4 12" />
            </svg>
          </span>
          <span className={styles.termsCheckboxLabel}>
            I agree to Dorsia&apos;s{' '}
            <button
              type="button"
              className={checkoutStyles.tcLink}
              onClick={() => setDorsiaTermsOpen(true)}
            >
              Terms of Use
            </button>
            {' '}and{' '}
            <button
              type="button"
              className={checkoutStyles.tcLink}
              onClick={() => setPrivacyPolicyOpen(true)}
            >
              Privacy Policy
            </button>
          </span>
        </label>

        {!termsAccepted && error && (
          <p className="text-sm text-red-500 mt-2">You must accept the terms to submit your request.</p>
        )}

        <button
          className={styles.ctaButton}
          onClick={handleSubmit}
          disabled={submitting || !widget.payment_method || !termsAccepted}
        >
          {submitting ? (
            <>
              <div className={styles.spinner} />
              Submitting...
            </>
          ) : (
            'Submit Request'
          )}
        </button>
      </div>

      {/* Modals */}
      <ContentModal
        isOpen={dorsiaTermsOpen}
        onClose={() => setDorsiaTermsOpen(false)}
        title="Terms of Use"
        content={TERMS_OF_USE}
      />
      <ContentModal
        isOpen={privacyPolicyOpen}
        onClose={() => setPrivacyPolicyOpen(false)}
        title="Privacy Policy"
        content={PRIVACY_POLICY}
      />
    </div>
  );
};

export default CustomRequestCheckoutPage;
